import { useTranslation } from 'react-i18next'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { EVENT_KEY_ESCAPE } from '../../../constants/event'
import { SIZE } from '../../../constants/size'
import {
  useKeyboardListener,
  useLinkLikeClick,
  useUserData,
} from '../../../hooks'
import Button from '../../Button'
import { useAsyncEntity } from '../../Entity'
import Modal, { Box, Buttons, Content, Header } from '../../Modal'
import { NotificationInline } from '../../Notification'
import {
  getApplicantProfileLink,
  getMockApplicantDetail,
} from '../../RentalPortalApplicant'
import {
  getViewingProfileLink,
  toViewingApplicantIdentity,
} from '../../RentalPortalViewing'

import {
  QA_NAME_VIEWING_APPLICANT_PREVIEW,
  QA_NAME_VIEWING_APPLICANT_PREVIEW_OPEN_PROFILE,
  QA_NAME_VIEWING_APPLICANT_PREVIEW_OPEN_VIEWING,
  QA_NAME_VIEWING_APPLICANT_PREVIEW_RETRY,
} from './RentalPortalViewingApplicantPreviewModal.constants'
import { getViewingApplicantPreviewSections } from './RentalPortalViewingApplicantPreviewModal.utils'
import { ViewingApplicantPreviewIdentity } from './ViewingApplicantPreviewIdentity'
import { ViewingApplicantPreviewSection } from './ViewingApplicantPreviewSection'
import { ViewingApplicantPreviewSkeleton } from './ViewingApplicantPreviewSkeleton'
import { ViewingApplicantPreviewViewing } from './ViewingApplicantPreviewViewing'

import * as styles from './RentalPortalViewingApplicantPreviewModal.module.css'

import ArrowRightIcon from '../../../icons/chevrons/right.svg'

function RentalPortalViewingApplicantPreviewModal({ viewing, onClose }) {
  const { t } = useTranslation()

  const { handleClick: handleLinkClick } = useLinkLikeClick()

  const currentUser = useUserData()

  const applicantId = viewing.applicant.id

  function getProfileData() {
    return getMockApplicantDetail({ applicantId, currentUser })
  }

  const { entityData, handleEntityReload, isInitiated, serverError } =
    useAsyncEntity({ entityId: applicantId, getProfileData })

  useKeyboardListener({
    callback: onClose,
    dependencies: [onClose],
    eventKey: EVENT_KEY_ESCAPE,
  })

  function handleOpenViewing(event) {
    onClose()

    handleLinkClick({ event, link: getViewingProfileLink(viewing.id) })
  }

  function handleOpenProfile(event) {
    onClose()

    handleLinkClick({ event, link: getApplicantProfileLink(applicantId) })
  }

  const applicant = entityData ?? toViewingApplicantIdentity(viewing.applicant)

  return (
    <Modal qaName={QA_NAME_VIEWING_APPLICANT_PREVIEW} onClose={onClose}>
      <Box size={SIZE.MEDIUM}>
        <Header
          text={t('RentalPortal.ApplicantPreviewTitleKey')}
          onClose={onClose}
        />
        <Content
          className={styles.content}
          contentWrapperClassName={styles.contentWrapper}
        >
          <ViewingApplicantPreviewIdentity
            applicant={applicant}
            stage={entityData?.stage}
          />
          <ViewingApplicantPreviewViewing viewing={viewing} />
          {serverError ? (
            <NotificationInline
              actions={
                <Button.Outlined
                  qaName={QA_NAME_VIEWING_APPLICANT_PREVIEW_RETRY}
                  text={t('RentalPortal.ListingRetryBtnKey')}
                  onClick={handleEntityReload}
                />
              }
              text={t('RentalPortal.ApplicantPreviewErrorKey')}
              type={NOTIFICATION_TYPE.error}
            />
          ) : null}
          {isInitiated ? null : <ViewingApplicantPreviewSkeleton />}
          {entityData
            ? getViewingApplicantPreviewSections({
                applicantData: entityData,
                t,
              }).map(section => (
                <ViewingApplicantPreviewSection
                  key={section.key}
                  section={section}
                />
              ))
            : null}
        </Content>
        <Buttons>
          <Button.Outlined
            isGrey
            qaName={QA_NAME_VIEWING_APPLICANT_PREVIEW_OPEN_VIEWING}
            text={t('RentalPortal.ViewingProfileOpenBtnKey')}
            onClick={handleOpenViewing}
          />
          <Button.Contained
            afterIconProps={{ Icon: ArrowRightIcon }}
            qaName={QA_NAME_VIEWING_APPLICANT_PREVIEW_OPEN_PROFILE}
            text={t('RentalPortal.ApplicantPreviewOpenProfileBtnKey')}
            onClick={handleOpenProfile}
          />
        </Buttons>
      </Box>
    </Modal>
  )
}

export default RentalPortalViewingApplicantPreviewModal
